import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';

interface PaymentMatch {
  paymentQuickbooksId: string;
  paymentAmount: number;
  paymentDate: string;
  paymentCustomerName?: string;
  paymentMethod?: string;
  paymentReference?: string;
}

interface CandidateInvoice {
  invoiceId: string;
  invoiceNumber: string;
  customerName: string;
  amount: number;
  balance: number;
  dueDate?: string;
  score: number;
}

interface MatchPaymentDialogProps {
  open: boolean;
  payment: PaymentMatch;
  selectedInvoices: CandidateInvoice[];
  onClose: () => void;
  onConfirm: (notes?: string) => Promise<void>;
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const MatchPaymentDialog: React.FC<MatchPaymentDialogProps> = ({
  open,
  payment,
  selectedInvoices,
  onClose,
  onConfirm,
}) => {
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedTotal = selectedInvoices.reduce((sum, inv) => sum + inv.balance, 0);
  const amountMatch = Math.abs(payment.paymentAmount - selectedTotal) < 0.01;
  const withinTolerance = Math.abs(payment.paymentAmount - selectedTotal) <= payment.paymentAmount * 0.05;

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);

    try {
      await onConfirm(notes || undefined);
      setNotes('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to match payment');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setNotes('');
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Confirm Payment Match</DialogTitle>
      <DialogContent>
        {/* Payment Info */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Payment:
          </Typography>
          <Typography variant="h6">
            {formatCurrency(payment.paymentAmount)}
          </Typography>
          <Typography variant="body2">
            {payment.paymentCustomerName || 'Unknown Customer'} - {formatDate(payment.paymentDate)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {payment.paymentMethod || 'Unknown Method'}
            {payment.paymentReference && ` | Ref: ${payment.paymentReference}`}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {/* Selected Invoices */}
        <Typography variant="subtitle2" gutterBottom>
          Matching to {selectedInvoices.length} Invoice(s):
        </Typography>

        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Invoice</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Due Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedInvoices.map((invoice) => (
                <TableRow key={invoice.invoiceId}>
                  <TableCell>INV-{invoice.invoiceNumber}</TableCell>
                  <TableCell>{invoice.customerName}</TableCell>
                  <TableCell align="right">{formatCurrency(invoice.balance)}</TableCell>
                  <TableCell>{invoice.dueDate ? formatDate(invoice.dueDate) : 'N/A'}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography variant="body2" fontWeight="medium">Total</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="medium">
                    {formatCurrency(selectedTotal)}
                  </Typography>
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Amount Validation */}
        {amountMatch ? (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
            Invoice total matches payment amount exactly.
          </Alert>
        ) : withinTolerance ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Invoice total is within 5% of payment amount. Difference: {formatCurrency(Math.abs(payment.paymentAmount - selectedTotal))}
          </Alert>
        ) : (
          <Alert severity="error" sx={{ mb: 2 }}>
            Invoice total does not match payment amount. Difference: {formatCurrency(Math.abs(payment.paymentAmount - selectedTotal))}
            <br />
            You may still proceed, but please verify this is correct.
          </Alert>
        )}

        <TextField
          label="Notes (optional)"
          multiline
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          placeholder="Add any notes about this match..."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
        >
          Confirm Match
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MatchPaymentDialog;
